import {
  Button,
  Icon,
  Modal,
  SlideDown,
  Typography,
} from '@globalmed/atlas-ui';
import { useTranslation } from 'react-i18next';
import styles from './CloseWarningModal.module.css';
import { useMediaQuery } from '@/hooks/useMediaQuery';

type TProps = {
  isOpen: boolean;
  description: string;
  onCancel: () => void;
  onConfirm: () => void;
};

/**
 * Confirmation modal shown when closing a "Create X" modal would discard
 * unsaved input. Renders as a bottom SlideDown on small screens.
 * @param props - Component props.
 * @param props.isOpen - Controls whether the modal is visible.
 * @param props.description - The body text explaining what will be lost.
 * @param props.onCancel - Called when the user chooses to keep editing.
 * @param props.onConfirm - Called when the user confirms discarding the form.
 * @returns CloseWarningModal component.
 */
export const CloseWarningModal = ({
  isOpen,
  description,
  onCancel,
  onConfirm,
}: TProps) => {
  const { t } = useTranslation();
  const isSmallScreen = useMediaQuery('(max-width: 576px)');
  const ModalComponent = isSmallScreen ? SlideDown : Modal;

  return (
    <ModalComponent
      open={isOpen}
      title={
        <div className={styles.title}>
          <Icon
            name="XCircle"
            size="24px"
            color="var(--colors-error600)"
            decorative
          />
          <Typography fontSize="20px" fontWeight={600}>
            {t('create-organization-modal.are-you-sure')}
          </Typography>
        </div>
      }
      sx={{ width: isSmallScreen ? '100%' : '446px' }}
      body={
        <div className={styles.body}>
          <Typography sx={{ lineHeight: '20px' }} fontWeight={500}>
            {description}
          </Typography>
          <div className={styles.buttons}>
            <Button text={t('cancel')} variant="secondary" onClick={onCancel} />
            <Button text={t('close')} color="destructive" onClick={onConfirm} />
          </div>
        </div>
      }
    />
  );
};
